import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ProductCard from "./ProductCard";
import ShinyText from "./ShinyText";

const TAB_IDS = ["all", "chairs", "sofas", "tables"];

const TAB_FALLBACK = {
  all: "All",
  chairs: "Chairs",
  sofas: "Sofas",
  tables: "Tables",
};

function matchCategory(product, tabId) {
  if (tabId === "all") return true;
  const cat = (product?.category || "").toLowerCase().replace(/\s+/g, "");
  const tab = tabId.toLowerCase();
  // Collapse armchairs under chairs
  if (tab === "chairs") return cat === "chairs" || cat === "chair" || cat === "armchairs" || cat === "armchair";
  return cat === tab || cat.includes(tab);
}

/** Matches `Store.css` — 6 columns from 1600px; use 12 = 2×6 so rows look full. Below that, 5 cols → 10 = 2×5. */
const WEEKLY_PAGE_SIZE_WIDE_QUERY = "(min-width: 1600px)";

function useWeeklyPageSize() {
  const [pageSize, setPageSize] = useState(() => {
    if (typeof window === "undefined") return 10;
    return window.matchMedia(WEEKLY_PAGE_SIZE_WIDE_QUERY).matches ? 12 : 10;
  });

  useEffect(() => {
    const mq = window.matchMedia(WEEKLY_PAGE_SIZE_WIDE_QUERY);
    const sync = () => setPageSize(mq.matches ? 12 : 10);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return pageSize;
}

export default function WeeklyBestsellers({
  products,
  resolveUrl,
  onAddToCart,
  onToggleWishlist,
  isInWishlist,
  onQuickView,
}) {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = useWeeklyPageSize();

  const tabLabel = (id) =>
    id === "all"
      ? t("store.weeklyTabAll", { defaultValue: "All" })
      : t(`store.categories.${id}`, { defaultValue: TAB_FALLBACK[id] || id });

  const filtered = useMemo(
    () =>
      activeTab === "all"
        ? products
        : products.filter((p) => matchCategory(p, activeTab)),
    [products, activeTab]
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visibleProducts = useMemo(
    () => filtered.slice(page * pageSize, page * pageSize + pageSize),
    [filtered, page, pageSize]
  );

  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages - 1));
  }, [totalPages, pageSize]);

  const canGoPrev = page > 0;
  const canGoNext = page < totalPages - 1;

  return (
    <section
      className="store-weekly-bestsellers"
      aria-labelledby="weekly-bestsellers-heading"
      dir={isRtl ? "rtl" : "ltr"}
      lang={isRtl ? "ar" : "en"}
    >
      <div className="store-weekly-inner">
        <div className="store-weekly-header">
          <h2 id="weekly-bestsellers-heading" className="store-weekly-title">
            <span className="store-weekly-title-lead">
              {t("store.weeklyTitleLead", { defaultValue: "Weekly" })}
            </span>{" "}
            <span
              className="store-weekly-title-accent"
              dir={isRtl ? "rtl" : "ltr"}
              lang={isRtl ? "ar" : "en"}
            >
              <ShinyText
                text={t("store.weeklyTitleAccent", { defaultValue: "bestsellers" })}
                speed={2}
                delay={0}
                color="#6f6458"
                shineColor="#ffffff"
                spread={120}
                direction={isRtl ? "right" : "left"}
                yoyo={false}
                pauseOnHover={false}
                disabled={false}
              />
            </span>
          </h2>
          <div className="store-weekly-tabs" role="tablist">
            {TAB_IDS.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activeTab === id}
                aria-controls="weekly-bestsellers-list"
                id={`tab-${id}`}
                className={`store-weekly-tab${activeTab === id ? " active" : ""}`}
                onClick={() => setActiveTab(id)}
              >
                {tabLabel(id)}
              </button>
            ))}
          </div>
        </div>

        <div className="store-weekly-carousel-wrap">
          <button
            type="button"
            className="store-weekly-nav store-weekly-prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label={t("store.weeklyNavPrev", {
              defaultValue: "Previous products",
            })}
            disabled={!canGoPrev}
          >
            <i className="fa-solid fa-angle-left" aria-hidden="true" />
          </button>

          <div
            className="store-weekly-scroll"
            id="weekly-bestsellers-list"
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
          >
            {visibleProducts.length === 0 ? (
              <p className="store-weekly-empty">
                {t("store.weeklyEmptyCategory", {
                  defaultValue: "No products in this category.",
                })}
              </p>
            ) : (
              visibleProducts.map((product, index) => (
                <div key={product._id} className="store-weekly-card">
                  <ProductCard
                    product={product}
                    onAddToCart={onAddToCart}
                    resolveUrl={resolveUrl}
                    animationDelay={index * 50}
                    onToggleWishlist={onToggleWishlist}
                    isInWishlist={isInWishlist}
                    onQuickView={onQuickView}
                  />
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            className="store-weekly-nav store-weekly-next"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            aria-label={t("store.weeklyNavNext", {
              defaultValue: "Next products",
            })}
            disabled={!canGoNext}
          >
            <i className="fa-solid fa-angle-right" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}
